<template>
    <on-click-outside :do="scope.close" v-if="enabled">
        <on-focus-outside :do="scope.close">
            <on-keydown :do="scope.close" :condition="key => key === 'Esc' || key === 'Escape'">
                <div :class="containerClass" @mouseover="() => isOver = true" @mouseleave="() => isOver = false">
                    <!-- Toggle button. -->
                    <div ref="trigger" :class="triggerClass">
                        <slot v-bind="scope"></slot>
                    </div>

                    <!-- Dropdown menu. -->
                    <div v-show="opened" ref="menu" id="menu"
                         class="absolute z-50 bg-white rounded-lg border border-gray-300 shadow-lg" :class="menuClass">
                        <slot name="menu" v-bind="scope"></slot>
                        <div id="arrow" data-popper-arrow v-if="arrow"></div>
                    </div>
                </div>
            </on-keydown>
        </on-focus-outside>
    </on-click-outside>
    <div ref="trigger" class="relative" v-else :class="triggerClass">
        <slot v-bind="scope"></slot>
    </div>
</template>

<script>
import {createPopper} from '@popperjs/core';

export default {
    props: {
        containerClass: {
            type: String,
            default: 'relative',
        },
        triggerClass: String,
        menuClass: String,
        placement: {
            type: String,
            default: 'bottom',
        },
        offset: {
            type: null,
            default: () => [0, 8],
        },
        arrow: {
            type: Boolean,
            default: true,
        },
        closeOnScroll: {
            type: Boolean,
            default: false
        },
        closeOnMouseOutScroll: {
            type: Boolean,
            default: false
        },
        enabled: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            opened: false,
            popperInstance: null,
            isOver: false,
        }
    },
    watch: {
        enabled: {
            handler(newValue, oldValue) {
                if (newValue) {
                    if (!this.popperInstance) {
                        this.createInstance()
                    }
                } else {
                    if (this.popperInstance) {
                        this.close()
                        this.popperInstance.destroy()
                        this.popperInstance = null
                    }
                }
            },
        },
    },
    computed: {
        scope() {
            return {
                opened: this.opened,
                open: this.open,
                close: this.close,
                toggle: this.toggle,
            }
        }
    },
    methods: {
        closeFromScroll() {
            if (!this.closeOnMouseOutScroll || !this.isOver) {
                this.close()
            }
        },
        open() {
            if (!this.opened) {
                this.$emit('opened')
                if (this.closeOnScroll || this.closeOnMouseOutScroll) {
                    window.addEventListener('scroll', this.closeFromScroll, true);
                }
            }
            this.opened = true
            this.setPopperEventListeners(true)
            this.popperInstance?.update()
        },
        close() {
            if (this.opened) {
                if (this.closeOnScroll || this.closeOnMouseOutScroll) {
                    window.removeEventListener('scroll', this.closeFromScroll, true);
                }
                this.$emit('closed')
            }
            this.opened = false
            this.setPopperEventListeners(false)
        },
        toggle() {
            this.opened ? this.close() : this.open()
        },
        setPopperEventListeners(enabled) {
            this.popperInstance?.setOptions(options => ({
                ...options,
                modifiers: [
                    ...(options.modifiers ?? []),
                    {name: 'eventListeners', enabled},
                ],
            }));
        },
        createInstance() {
            if (this.popperInstance) {
                this.setPopperEventListeners(false)
                this.popperInstance.destroy()
                this.popperInstance = null
            }
            if (this.$refs.trigger && this.$refs.menu) {
                this.popperInstance = createPopper(this.$refs.trigger, this.$refs.menu, {
                    placement: this.placement,
                    modifiers: [
                        {name: 'preventOverflow', options: {padding: 16}},
                        {name: 'offset', options: {offset: this.offset}},
                        {name: 'arrow', options: {padding: 8}},
                    ],
                });
                this.setPopperEventListeners(true)
            } else {
                this.$nextTick(() => {
                    this.createInstance()
                })
            }
        }
    },
    mounted() {
        if (this.enabled) {
            this.createInstance()
        }
    },
    beforeDestroy() {
        if (this.popperInstance) {
            this.setPopperEventListeners(false)
            this.popperInstance.destroy()
            this.popperInstance = null
            window.removeEventListener('scroll', this.closeFromScroll, true);
        }
    },
}
</script>

<style scoped>
#arrow,
#arrow::before {
    position: absolute;
    width: 10px;
    height: 10px;
    background: inherit;
}

#arrow {
    visibility: hidden;
}

#arrow::before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
    border: 1px solid #d1d5db;
}

#menu[data-popper-placement^='top'] > #arrow {
    bottom: -6px;
}

#menu[data-popper-placement^='top'] > #arrow::before {
    border-top: none;
    border-left: none;
}

#menu[data-popper-placement^='bottom'] > #arrow {
    top: -6px;
}

#menu[data-popper-placement^='bottom'] > #arrow::before {
    border-bottom: none;
    border-right: none;
}

#menu[data-popper-placement^='left'] > #arrow {
    right: -6px;
}

#menu[data-popper-placement^='left'] > #arrow::before {
    border-bottom: none;
    border-left: none;
}

#menu[data-popper-placement^='right'] > #arrow {
    left: -6px;
}

#menu[data-popper-placement^='right'] > #arrow::before {
    border-top: none;
    border-right: none;
}
</style>
